// MediaLibraryReuse — the ONE shared "Reuse from your Media Library" affordance
// (DL-048 adoption unit, Gordon "one unit, all of them" 2026-07-14). It packages
// the flag gate + the pill button + the data-connected deck modal so that EVERY
// media-attaching surface (Segment wallpaper, Video/Image blocks, Business Box,
// rich-text insert, …) mounts the exact same reuse experience through a single
// component — never a per-surface reimplementation (everything-is-a-shared-component).
//
// The reuse-in-context modal shipped in #1989 (deck states 03 grid / 06 list) and
// was mounted at the shared MediaSelection layer; the surfaces that do NOT render
// MediaSelection adopt it via THIS component. Flag-gated on MEDIA_LIBRARY_PICKER_ENABLED,
// default OFF: when OFF this component renders NOTHING (returns null before any state
// or modal), so every consumer stays byte-identical to today.
//
// acceptedTypes is derived by each call site from its EXISTING media-type restriction
// (image / video / audio / document) — never invented here. segmentName (F2) is passed
// by call sites that know their open Segment so the modal's context chip names it.
import React, { useState } from "react";
import styled from "styled-components";
import { MEDIA_LIBRARY_PICKER_ENABLED } from "../../constants/featureFlags";
import MediaLibraryPickerConnected from "./MediaLibraryPickerConnected";
import { IconLibrary } from "./icons";
import { toInsertValue } from "../SegmentBuilder/Shared/MediaSelection/librarySourceLogic";

// Visual parity with the #1989 MediaSelection affordance (same 32px pill, purple
// keyline, DM Sans). Kept here as the single definition now every surface shares it.
const ReuseLibraryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 12px;
  padding: 7px 14px;
  min-height: 32px;
  border: 1px solid #9b5af8;
  border-radius: 8px;
  background: #fbfaff;
  color: #6f27d6;
  font: 600 13px "DM Sans", system-ui, sans-serif;
  cursor: pointer;
  transition: background 120ms ease;
  & svg {
    flex: 0 0 auto;
  }
  &:hover {
    background: #efe9fe;
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(155, 90, 248, 0.35);
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export default function MediaLibraryReuse({
  acceptedTypes = null,
  segmentName = null,
  contextLabel = null,
  entryLabel,
  // onInsert receives the normalized value shape (toInsertValue) — the SAME shape
  // the Upload/Browse/Recent sources hand every downstream consumer. Use onInsertItem
  // to receive the raw Media Library item (e.g. a rich-text surface that builds its
  // own HTML from url + type).
  onInsert,
  onInsertItem,
  label = "Reuse from your Media Library",
  className,
  style,
  buttonTestId = "open-media-library",
}) {
  const [open, setOpen] = useState(false);

  // Flag OFF -> render nothing at all (no button, no modal, no query). This is what
  // keeps every adopting surface byte-identical to today when the flag is OFF.
  if (!MEDIA_LIBRARY_PICKER_ENABLED) return null;

  return (
    <>
      <ReuseLibraryBtn
        type="button"
        className={className}
        style={style}
        onClick={() => setOpen(true)}
        data-testid={buttonTestId}
      >
        <IconLibrary size={16} /> {label}
      </ReuseLibraryBtn>
      <MediaLibraryPickerConnected
        open={open}
        acceptedTypes={acceptedTypes}
        segmentName={segmentName}
        contextLabel={contextLabel}
        entryLabel={entryLabel}
        onClose={() => setOpen(false)}
        onInsert={(item) => {
          if (onInsertItem) onInsertItem(item);
          if (onInsert) onInsert(toInsertValue(item));
          setOpen(false);
        }}
      />
    </>
  );
}
